/**
 * Incoming-chat alert chime, generated with the Web Audio API so no binary
 * asset has to ship. A short, professional two-note rising motif (A5 → D6)
 * with a soft sine timbre and quick decay — attention-getting without being
 * harsh. Fully feature-detected and wrapped so it can never throw into a
 * React event handler.
 *
 * Browsers block audio until the user has interacted with the page; the first
 * play after a gesture (any click) succeeds, which is fine for agents working
 * in the dashboard.
 */
export function playIncomingChime(): void {
  if (typeof window === 'undefined') return
  // Respect the existing notification-sound preference toggle.
  try {
    if (localStorage.getItem('vela.notify.sound') === 'off') return
  } catch {
    /* localStorage unavailable — fall through and still try to play */
  }
  try {
    const Ctx =
      window.AudioContext ??
      (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext
    if (!Ctx) return
    const ctx = new Ctx()
    const now = ctx.currentTime
    const notes = [
      { freq: 880.0, at: 0 },     // A5
      { freq: 1174.66, at: 0.16 }, // D6
    ]
    for (const n of notes) {
      const osc = ctx.createOscillator()
      const gain = ctx.createGain()
      osc.type = 'sine'
      osc.frequency.value = n.freq
      osc.connect(gain)
      gain.connect(ctx.destination)
      const start = now + n.at
      gain.gain.setValueAtTime(0.0001, start)
      gain.gain.exponentialRampToValueAtTime(0.14, start + 0.02)
      gain.gain.exponentialRampToValueAtTime(0.0001, start + 0.34)
      osc.start(start)
      osc.stop(start + 0.36)
    }
    // Release the context shortly after the sound finishes.
    window.setTimeout(() => { void ctx.close().catch(() => undefined) }, 900)
  } catch {
    /* audio not available */
  }
}
